<template>
  <div class="plaid-link-wrapper">
    <button
      :class="['plaid-link-btn', buttonClass, { 'plaid-link-btn--loading': loading, 'plaid-link-btn--disabled': disabled || loading }]"
      :disabled="disabled || loading"
      @click="openPlaidLink"
    >
      <span v-if="loading" class="plaid-link-btn__spinner">
        <i class="fas fa-circle-notch fa-spin"></i>
      </span>
      <span v-else class="plaid-link-btn__icon">
        <i :class="buttonIcon"></i>
      </span>
      <span class="plaid-link-btn__label">{{ buttonLabel }}</span>
    </button>

    <!-- Re-auth mode: shown when item status is ERROR -->
    <p v-if="showReauthHint" class="plaid-link-reauth-hint">
      <i class="fas fa-exclamation-triangle text-yellow-500"></i>
      Your bank connection needs to be refreshed.
    </p>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'PlaidLinkButton',

  props: {
    /**
     * Use case determines which Plaid products are requested.
     * Must match PlaidItem.UseCase choices on the backend.
     */
    useCase: {
      type: String,
      default: 'COUPLES_CONNECT',
      validator: (v) => [
        'COUPLES_CONNECT',
        'CONSTRUCTION',
        'BUYER_QUALIFICATION',
        'VENDOR_KYC',
        'BILLING',
      ].includes(v),
    },

    /** Label shown on the button */
    label: {
      type: String,
      default: 'Connect Bank Account',
    },

    /** Icon class (Font Awesome) */
    icon: {
      type: String,
      default: 'fas fa-university',
    },

    /** Additional CSS class for the button */
    buttonClass: {
      type: String,
      default: 'btn btn-primary',
    },

    /** Disable the button externally */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * When true, shows a re-auth hint (for ERROR status items).
     * Use when re-launching Link to reconnect an existing item.
     */
    showReauthHint: {
      type: Boolean,
      default: false,
    },

    /** OAuth redirect URI — required for OAuth-based institutions on mobile */
    redirectUri: {
      type: String,
      default: null,
    },
  },

  emits: [
    /** Emitted on successful connection: { item_id, plaid_item_id, institution_name, accounts } */
    'success',
    /** Emitted if user exits without connecting */
    'exit',
    /** Emitted if an error occurs before/during Link */
    'error',
  ],

  data() {
    return {
      loading: false,
      linkHandler: null,
    };
  },

  computed: {
    buttonLabel() {
      return this.loading ? 'Connecting...' : this.label;
    },
    buttonIcon() {
      return this.loading ? 'fas fa-circle-notch fa-spin' : this.icon;
    },
  },

  beforeUnmount() {
    if (this.linkHandler) {
      this.linkHandler.destroy();
    }
  },

  methods: {
    async openPlaidLink() {
      if (this.loading || this.disabled) return;

      this.loading = true;
      try {
        // Step 1: Get a link token from our backend
        const tokenResponse = await api.post('/users/plaid/link-token/', {
          use_case: this.useCase,
          redirect_uri: this.redirectUri,
        });

        const linkToken = tokenResponse.data.link_token;
        if (!linkToken) {
          throw new Error('No link token received from server');
        }

        // Step 2: Load Plaid Link script if not already loaded
        await this.loadPlaidScript();

        // Step 3: Initialize Plaid Link
        this.linkHandler = window.Plaid.create({
          token: linkToken,
          onSuccess: this.handleSuccess,
          onExit: this.handleExit,
          onEvent: this.handleEvent,
        });

        this.loading = false;
        this.linkHandler.open();
      } catch (error) {
        this.loading = false;
        console.error('PlaidLinkButton error:', error);
        this.$emit('error', { error: error.message || 'Failed to initialize Plaid Link' });
      }
    },

    async handleSuccess(publicToken, metadata) {
      this.loading = true;
      try {
        // Step 4: Exchange public token with our backend
        const exchangeResponse = await api.post('/users/plaid/exchange-token/', {
          public_token: publicToken,
          use_case: this.useCase,
        });

        this.$emit('success', exchangeResponse.data);
      } catch (error) {
        console.error('PlaidLinkButton exchange error:', error);
        this.$emit('error', {
          error: 'Failed to save bank connection. Please try again.',
          metadata,
        });
      } finally {
        this.loading = false;
      }
    },

    handleExit(err, metadata) {
      this.loading = false;
      if (err) {
        console.warn('Plaid Link exited with error:', err);
      }
      this.$emit('exit', { err, metadata });
    },

    handleEvent(eventName, metadata) {
      // Useful for analytics — log Link funnel events
      if (import.meta.env.DEV) {
        console.debug('Plaid Link event:', eventName, metadata);
      }
    },

    loadPlaidScript() {
      return new Promise((resolve, reject) => {
        if (window.Plaid) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = () => reject(new Error('Failed to load Plaid Link script'));
        document.head.appendChild(script);
      });
    },
  },
};
</script>

<style scoped>
.plaid-link-wrapper {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
}

.plaid-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.plaid-link-btn--loading,
.plaid-link-btn--disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.plaid-link-btn__spinner,
.plaid-link-btn__icon {
  width: 1rem;
  text-align: center;
}

.plaid-link-reauth-hint {
  font-size: 0.8rem;
  color: #b45309;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin: 0;
}
</style>
